import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  formatAmountCentsBasedOnCurrency,
  FormCheckbox,
  getTermsAndConditionsURL,
  InputViewTwo,
  ModalView,
  ONE_CLICK_TERMS_AND_CONDITIONS_URL,
  TermsConditions,
  useCheckoutDisplayAmount,
  useSafeSelector,
} from '@unified-checkout/kit-components';
import { ExpiryDate } from '../../components/ExpiryDate/ExpiryDate';
import { CVV } from '../../components/CVV/CVV';
import { CardHolder } from '../../components/CardHolder/CardHolder';
import {
  CARD_TYPES,
  AMEX_CARD_LENGTH,
  CARD_NUMBER_SPACES,
  CARD_LENGTH,
  EXPIRY_DATE_LENGTH,
  AMEX_CVV_LENGTH,
  CVV_LENGTH,
  CARD_HOLDER_NAME_MAX_LENGTH,
  SAVED_CARD_TYPES,
} from '../../constants';
import valid from 'card-validator';
import { MobileCardNumber } from '../../components/CardNumber/MobileCardNumberViewTwo';
import cvvIcon from '../../assets/icons/cvv-info.svg';
import styles from './mobileCardCvv.module.scss';
import visaCVV from '../../assets/icons/card-cvv.png';
import amexCVV from '../../assets/icons/amex-cvv.png';
import closeIcon from '../../assets/icons/close.png';
import InstantRefundOpt from '../../components/InstantRefundOpt';
import InfoIcon from '../../assets/icons/Vector.png';
import { CardToInstallment, InstallmentContext, INSTALLMENT_BIN_LENGTH } from './cardToInstallment';
// import {Input} from '@unified-checkout/design-system'

export function MobileCard(props: any) {
  const {
    onSubmit,
    loading,
    customStyle,
    setIsOmanNetCard,
    useHookForm,
    getCardLogo,
    setIsValidVisa,
    showTerms,
    onDiscount,
    onFees,
    discountAppliedMessage,
    discountErrorMessage,
    hasOmanNetIntegration,
    showSaveCard,
    forceSaveCard,
    content,
    noCardLogo,
    noSubmitBtn,
    buttonLabel,
    isDisabled,
    splitPayments,
    currency,
    CardPaymentWrapper,
    amount,
    bankTermsAndConditions,
    isBankTermsAgreed,
    onCheckInstantRefund,
    onInstantRefund,
    onCheckInstallmentPlans,
    onSelectedInstallmentPlanChange,
    hideInstallmentPlansUI,
  } = props;
  const { t } = useTranslation('card');
  const { t: tPaymentLayout } = useTranslation('flash', { keyPrefix: 'paymentLayout' });
  const [cardNumberSelected, setCardNumberSelected] = useState(false);
  const [validCard, setValidCard] = useState(false);
  const [showName, setShowName] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const {
    isSplitPaymentsView,
    currentCardNumber,
    splitCardCount,
    SplitPaymentButton,
  } = splitPayments || {};

  const isLastCard = currentCardNumber === splitCardCount;

  const { checkBinFees, feesAmount, subType } = useSafeSelector((state: any) => state.payment);
  const intentionData = useSafeSelector((state: any) => state.intention);
  const hasCampaignDiscount = useSafeSelector((state: any) => state.discounts.hasCampaignDiscount);
  const { showInstantRefund } = useSafeSelector((state: any) => state.paymentDetails);
  const { fastCheckoutAccount, isFastCheckoutView, isConsentChecked  } = useSafeSelector((state: any) => state.fastCheckout);
  const canUseFastCheckout = fastCheckoutAccount?.canUseFastCheckout;
  const lang = useSafeSelector((state: any) => state.lang);
  const langDirection = lang === 'ar' ? 'rtl' : 'ltr';
  const {
    amount: checkoutAmount,
    displayCurrency,
    currencyFontFamily,
    currencyClassName,
  } = useCheckoutDisplayAmount('card');
  const [installmentContext, setInstallmentContext] = useState<InstallmentContext>({
    hasSelectedPlan: false,
    selectedPlanData: null,
    hasLoadedPlans: false,
  });
  const { hasSelectedPlan: isInstallmentPlanSelected, selectedPlanData, hasLoadedPlans } = installmentContext;

  const styling = {
    container: {
      backgroundColor: customStyle?.backgroundColor,
      fontFamily: customStyle?.fontFamily,
    },
    input: {
      color: customStyle?.fieldColor,
      borderColor: customStyle?.borderColor,
      direction: langDirection
    },
    button: {
      backgroundColor: customStyle?.buttonColor,
    },
  };

  const onClose = () => {
    setShowModal(false);
  };

  const {
    handleSubmit,
    errors,
    isDirty,
    isValid,
    cardNumberRegister,
    expiryRegister,
    cvcRegister,
    nameRegister,
    saveCardRegister,
    getFieldState,
    setFocus,
    resetField,
    trigger,
    clearErrors,
    getValues,
    cardType,
    watch,
    amountRegister,
    setValue
  } = useHookForm({
    ...(isSplitPaymentsView && { isAmount: true, maxAmount: amount }),
    isStandardCard: true,
    hasOmanNetIntegration,
    noCardLogo,
    ...(isSplitPaymentsView && isLastCard && { defaultValues: { amount } }),
  });

  const numberWatch = watch('number');
  const expiryWatch = watch('expiry');
  const cvcWatch = watch('cvc');

  const emptyState = () => {
    if (getValues('number') === '') return !cardNumberSelected;
    return false;
  };

  const cardNum = getValues('number');
  const cardBin = (cardNum?.replace(/\s/g, '') ?? '').slice(0, INSTALLMENT_BIN_LENGTH);
  const isValidCard = valid.number(cardNum).isValid;
  const isValidVisa = isValidCard && cardType?.toUpperCase() === SAVED_CARD_TYPES.VISA;
  const isValidExpiry = valid.expirationDate(expiryWatch).isValid;

  useEffect(() => {
    if (isValidVisa) setIsValidVisa?.(true);
    else setIsValidVisa?.(false);
  }, [isValidVisa]);

  useEffect(() => {
      setIsOmanNetCard?.(cardType === CARD_TYPES.OMAN || cardType === CARD_TYPES.MAAL);
  }, [cardType, setIsOmanNetCard]);

  useEffect(() => {
    if (numberWatch) {
      const trimmedNumber = numberWatch.replace(/ /g, '').trim();
      const numberValidation = valid.number(trimmedNumber);
      if (numberValidation.isValid) {
        setValidCard(true);
        trigger('number');
        setCardNumberSelected(false);
        const timerId = setTimeout(() => {
          setFocus('expiry');
          clearTimeout(timerId);
        }, 10);
        return;
      }
      setValidCard(false);
      if (cardType === CARD_TYPES.AMEX) {
        if (numberWatch.replace(/ /g, '').trim().length === AMEX_CARD_LENGTH) {
          trigger('number');
        }
      } else {
        if (numberWatch.replace(/ /g, '').trim().length === CARD_LENGTH) {
          trigger('number');
        }
      }
    }
  }, [numberWatch]);

  useEffect(() => {
    if (expiryWatch && cvcWatch) {
      if (
        valid.expirationDate(expiryWatch).isValid &&
        getValues('expiry') !== ''
      ) {
        if (cardType === CARD_TYPES.AMEX) {
          if (cvcWatch.length === AMEX_CVV_LENGTH) {
            setShowName(true);
            setFocus('name');
          } else setShowName(false);
        } else {
          if (cvcWatch.length === CVV_LENGTH) {
            setShowName(true);
            const timerId = setTimeout(() => {
              setFocus('name');
              clearTimeout(timerId);
            }, 100);
          } else setShowName(false);
        }
      }
    }
  }, [expiryWatch, cvcWatch]);

useEffect(() => {
    const hasFeesAmount = !!(feesAmount || subType?.feesAmount);
    const cardData = {
      cardNum,
      isValidCard,
      cardType,
      ...(hasCampaignDiscount && { cardExpiry: expiryWatch, isValidExpiry }),
      hasFeesAmount
    };

    if (!checkBinFees && !hasFeesAmount) {
      if (showInstantRefund) {
        onCheckInstantRefund?.(cardData);
      } else {
        onDiscount?.(cardData);
      }
    } else if (checkBinFees || hasFeesAmount) {
      onFees?.(cardData);
    }
  }, [
    isValidCard,
    cardNum,
    cardType,
    hasCampaignDiscount && expiryWatch,
    hasCampaignDiscount && isValidExpiry,
  ]);

  useEffect(() => {
    clearErrors();
    if (isSplitPaymentsView && isLastCard) {
      setValue('amount', amount);
    }
  }, [isSplitPaymentsView]);

  const handleInstantRefundToggle = (isInstantRefundEnabled: boolean) => {
    onInstantRefund({
      isInstantRefundEnabled,
      cardNum,
      isValidCard,
      cardType,
      ...(hasCampaignDiscount && { cardExpiry: expiryWatch, isValidExpiry }),
    });
  };

  const restAmount = intentionData.split_pay_details?.total_auth_amount_paid ?  formatAmountCentsBasedOnCurrency(
    intentionData.split_pay_details?.total_auth_amount_left || 0,
    currency
  ) : undefined

  const termsAndConditionsURL = getTermsAndConditionsURL() 

  const cardContent = (
    <>
      <p className="text-sm font-[500] mb-2 text-gray-700">{t('CARD_INFO')}</p>
      <div
        onFocus={(e) => {
          if (
            e.target.id === 'name' &&
            (getValues('expiry') === '' || getValues('cvc') === '')
          ) {
            resetField('expiry');
            resetField('cvc');
          } else if (e.target.id === 'expiry') {
            clearErrors('expiry');
            resetField('expiry');
          } else if (e.target.id === 'cvc') {
            clearErrors('cvc');
            resetField('cvc');
          }
          setCardNumberSelected(true);
          if (getValues('number') === '') {
            setFocus('number');
          }
        }}
        onBlur={() => {
          setCardNumberSelected(false);
        }}
        className={`flex gap-2 border rounded-lg px-3 ${
          showName ? 'rounded-b-none' : ''
        } ${
          errors['number'] || errors['expiry'] || errors['cvc']
            ? 'border-red-300'
            : 'border-gray-300'
        } ${errors['name'] ? 'border-b-red-300' : 'border-b-gray-300'} ${
          isSplitPaymentsView ? 'bg-white' : ''
        }`}
      >
        <div
          onClick={() => {
            setCardNumberSelected(true);
            const timerId = setTimeout(() => {
              setFocus('number');
              clearTimeout(timerId);
            }, 100);
          }}
          className={cardNumberSelected ? 'w-full' : 'w-3/5'}
        >
          <MobileCardNumber
            noCardLogo={noCardLogo}
            formRegister={cardNumberRegister}
            cardType={cardType}
            getCardLogo={getCardLogo}
            maxLength={
              cardType === CARD_TYPES.AMEX
                ? AMEX_CARD_LENGTH + CARD_NUMBER_SPACES
                : CARD_LENGTH + CARD_NUMBER_SPACES
            }
            customStyle={styling.input}
            className={`border-0 !gap-2 flex-grow !px-0`}
            customInputStyle={`!pr-0 !text-sm ${
              validCard && !cardNumberSelected ? 'hidden' : ''
            }`}
            label={
              validCard &&
              !cardNumberSelected && (
                <p className="flex-grow w-full cursor-pointer justify-self-start">
                  {`${'*'.repeat(4)} ${
                    getValues('number')
                      ? getValues('number').substring(
                          getValues('number').length - 4
                        )
                      : ''
                  }`}
                </p>
              )
            }
          />
        </div>
        {!emptyState() && (
          <div
            onFocus={(e) => {
              e.stopPropagation();
            }}
            className={`flex gap-2 w-2/5 ${
              cardNumberSelected ||
              (!cardNumberSelected &&
                getFieldState('number').isTouched &&
                getFieldState('number').invalid)
                ? 'invisible'
                : ''
            }`}
          >
            <ExpiryDate
              formRegister={expiryRegister}
              maxLength={EXPIRY_DATE_LENGTH}
              expiryDate={getValues('expiry')}
              customStyle={styling.input}
              className={`!px-0 border-0`}
              customInputStyle={'!pr-0 !text-sm'}
            />
            <CVV
              formRegister={cvcRegister}
              maxLength={
                cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH
              }
              customStyle={styling.input}
              className={`!px-0 border-0`}
              customInputStyle={'!pr-0 !text-sm'}
            />
          </div>
        )}
        {emptyState() && (
          <div
            onClick={() => {
              setCardNumberSelected(true);
              setFocus('number');
            }}
            className="flex justify-end w-2/5 gap-2"
          >
            <p className="flex items-center justify-center text-sm text-gray-500 cursor-default">
              MM/YY
            </p>
            <p className="flex items-center justify-center gap-1 text-sm text-gray-500 cursor-default">
              CVV
              <img
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowModal(true);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setShowModal(true);
                }}
                src={cvvIcon}
                alt="cvv info"
                className="object-contain w-3 h-3 cursor-pointer"
              />
            </p>
          </div>
        )}
      </div>
      <div className={`w-full overflow-hidden ${showName ? '' : 'hidden'}`}>
        <CardHolder
          formRegister={nameRegister}
          maxLength={CARD_HOLDER_NAME_MAX_LENGTH}
          error={errors['name']}
          customStyle={styling.input}
          className={`rounded-lg border-t-0 rounded-t-none ${
            errors['name'] ? 'border-red-300' : 'border-gray-300'
          }`}
        />
      </div>
      {isInstallmentPlanSelected &&
        (discountAppliedMessage || discountErrorMessage) &&
        !isSplitPaymentsView && (
          <div className="mt-2 flex items-start gap-2 text-gray-600 text-sm leading-[1.125rem] track-[0.01rem]">
            <img src={InfoIcon} alt="info" className="mt-[2px] h-4 w-4" />
            <p>{t('DISCOUNT_NOT_APPLICABLE_FOR_INSTALLMENTS')}</p>
          </div>
        )}
      {!isInstallmentPlanSelected && discountErrorMessage && (
        <p className="mt-2 text-orange-700 text-sm leading-[1.125rem] track-[0.01rem]">
          {discountErrorMessage}
        </p>
      )}
      {!isInstallmentPlanSelected && discountAppliedMessage && !isSplitPaymentsView && !canUseFastCheckout && (
        <p className="mt-2 text-green-400 text-sm leading-[1.125rem] track-[0.01rem]">
          {discountAppliedMessage}
        </p>
      )}
      {(errors['number'] ||
        errors['cvc'] ||
        errors['expiry'] ||
        errors['name']) && (
        <ul className="my-2 text-xs text-red-600 list-disc list-inside list-item:text-red-600">
          {errors['number'] && <li>{errors['number'].message}</li>}
          {errors['expiry'] && <li>{errors['expiry'].message}</li>}
          {errors['cvc'] && <li>{errors['cvc'].message}</li>}
          {errors['name'] && <li>{errors['name'].message}</li>}
        </ul>
      )}
    <CardToInstallment
        mode="bin"
        cardBin={cardBin}
        hideUI={!!hideInstallmentPlansUI}
        onCheckInstallmentPlans={onCheckInstallmentPlans}
        onSelectedInstallmentPlanChange={onSelectedInstallmentPlanChange}
        onInstallmentContextChange={setInstallmentContext}
        displayCurrency={displayCurrency}
        intentionCurrency={intentionData.currency}
        planAmountCurrency={currency}
        tPaymentLayout={tPaymentLayout}
        />

        {forceSaveCard && !isFastCheckoutView  && !isConsentChecked && (
          <p className="w-full mt-4 text-sm text-gray-700">
            {t('FORCE_SAVE_CARD')}
          </p>
        )}
      {!forceSaveCard && showSaveCard && !isSplitPaymentsView && !isConsentChecked && !isFastCheckoutView && (
        <div className="w-full mt-4">
          <FormCheckbox
            formRegister={saveCardRegister}
            label={t('SAVE_CARD')}
            name="save_card"
            customStyle={styling.input}
          />
        </div>
      )}
      {content}
      {isFastCheckoutView && (
        <p className="w-full mt-4 text-sm text-gray-700">
          <Trans
            i18nKey="ONE_CLICK_HINT_TERMS"
            t={t}
            components={{
              1: (
                <a
                  href={termsAndConditionsURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-900 underline underline-offset-2"
                />
              ),
            }}
          />
        </p>
      )}
      {showInstantRefund && !isSplitPaymentsView && !canUseFastCheckout && (
        <InstantRefundOpt
          onClick={handleInstantRefundToggle}
          isLoading={loading}
          forceDisabled={isInstallmentPlanSelected}
          forceDisabledMessage={t('INSTANT_REFUND_NOT_AVAILABLE_FOR_INSTALLMENTS')}
        />
      )}
      {SplitPaymentButton && (
        <div className="mt-4">
          <SplitPaymentButton />
        </div>
      )}
      {isSplitPaymentsView && (
        <div className='mt-4'>
          <p className="mb-2 text-gray-700 english-label-medium">
            {isLastCard
              ? t('REMAINING_AMOUNT')
              : t('ENTER_AMOUNT')}
          </p>
          <InputViewTwo
            type="number"
            name="amount"
            label={
              <div className="absolute flex items-center h-full">
                <p className="text-sm leading-4 text-gray-900 font-Inter">
                  <span style={{fontFamily:currencyFontFamily}}>{tPaymentLayout(displayCurrency)}</span>
                </p>
              </div>
            }
            placeholder="0.0"
            customStyle={{ gap: '1rem', borderColor: errors['amount'] ? '#F87171' : '' , backgroundColor: isLastCard ? '#F9FAFB' : 'white',
              color: isLastCard ? "#667085": ""
            }}
            disabled={isLastCard}
            max={amount}
            value={getValues().amount}
            {...amountRegister}
          />
            {restAmount && !isLastCard && <p className='font-inter text-sm leading-[18px] text-gray-600 mt-2'>{t('REMAINING_AMOUNT')} :
              <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '} {restAmount}</p>}
        </div>
      )}
      {errors['amount'] && (
        <ul className="my-2 text-xs text-red-600 list-disc list-inside list-item:text-red-600">
          {errors['amount'] && <li>{errors['amount'].message}</li>}
        </ul>
      )}
    </>
  );

  const handleFormSubmit = (data: any) => {
    onSubmit({
      ...data,
      ...(selectedPlanData && { tenure: String(selectedPlanData.months) }),
    });
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        className="flex flex-col w-full h-full max-w-full m-auto"
        style={styling.container}
      >
        {CardPaymentWrapper ? (
          <CardPaymentWrapper>{cardContent}</CardPaymentWrapper>
        ) : (
          cardContent
        )}
        <div className="w-full mt-auto">
        {showTerms && !isFastCheckoutView && (
            <TermsConditions/>
          )}
        {bankTermsAndConditions}
        {!noSubmitBtn && (
          <Button
            label={isSplitPaymentsView ? (
              buttonLabel || getValues('amount') ?
              <>
                  {t('CARD_ACTION_BTN') + " "}
                  <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '}
                  {getValues('amount')}
              </> : t('CARD_ACTION_BTN')) :
              selectedPlanData ? (
              <>
                {buttonLabel || t('CARD_ACTION_BTN')}{' '}
                <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '}
                {formatAmountCentsBasedOnCurrency(
                  selectedPlanData.amount,
                  currency
                )}
                {t('PAY_PER_MONTH')}
              </>
              ) : hasLoadedPlans ? (
              <>
                {buttonLabel || t('INSTALLMENT_PAY_IN_FULL')}{' '}
                <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '}
                {checkoutAmount}
              </>
              ) : (
              <>
                {buttonLabel || t('CARD_ACTION_BTN')}{' '}
                    {displayCurrency && (
                      <span className={currencyClassName}>
                        {tPaymentLayout(displayCurrency)}{' '}
                      </span>
                    )}
                {checkoutAmount}
              </>
              )
            }
            id="pay-button"
            loading={loading}
            fullWidth={true}
            disabled={isDisabled || loading || !isDirty || !isValid || (bankTermsAndConditions && !isBankTermsAgreed)}
            customStyle={styling.button}
          />
          )}
        </div>
      </form>
      {showModal && (
        <ModalView onClose={onClose} showModal={showModal}>
          <>
            <img
              className={styles['close']}
              src={closeIcon}
              onClick={onClose}
              alt="close cvv modal"
            />
            <h3 className={styles['modal-header']}>{t('CVV_MODAL_HEADER')}</h3>
            <div className={styles['modal-content']}>
              <h3 className={styles['modal-content-header']}>
                {t('CVV_MODAL_VISA_HEADER')}
              </h3>
              <p className={styles['modal-content-body']}>
                {t('CVV_MODAL_VISA_BODY')}
              </p>
              <img
                className={styles['modal-image']}
                src={visaCVV}
                alt="Master card / Visa card Back"
              />
              <h3 className={styles['modal-content-header']}>
                {t('CVV_MODAL_AMEX_HEADER')}
              </h3>
              <p className={styles['modal-content-body']}>
                {t('CVV_MODAL_AMEX_BODY')}
              </p>
              <img
                className={styles['modal-image']}
                src={amexCVV}
                alt="American Express card Front"
              />
            </div>
          </>
        </ModalView>
      )}
    </>
  );
}

export default MobileCard;
